import React from "react";
import {
  Button,
  Badge,
  EmptyState,
} from "../../components/AdminSharedComponents";
import {
  User,
  Calendar,
  Edit,
  Trash2,
  Plus,
  FileText,
  Eye,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  Navigation,
} from "lucide-react";
import StatusIcon from "./StatusIcon";
import ReportItem from "./ReportItem";

const ProjectCard = ({
  project,
  reports,
  onDeleteClick,
  onOpenReportModal,
  userRole,
  isExpanded,
  onToggleExpand,
}) => {
  const isSupervisor = userRole === "supervisor";
  const reportCount = reports?.length || 0;

  // Handle card click (expand/collapse)
  const handleCardClick = (e) => {
    // Prevent card click when clicking on action buttons
    if (
      e.target.closest(".action-button") ||
      e.target.closest(".dropdown-menu")
    ) {
      return;
    }
    onToggleExpand();
  };

  // Handle action button clicks with event stopping
  const handleActionClick = (e, action) => {
    e.stopPropagation();
    action();
  };

  return (
    <div
      className="group border border-gray-200 rounded-xl bg-white hover:shadow-lg hover:border-gray-300 transition-all duration-300 cursor-pointer"
      onClick={handleCardClick}
    >
      {/* Main Project Info */}
      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 lg:gap-4">
          {/* Left Section */}
          <div className="flex-1 space-y-3">
            {/* Project Header */}
            <div className="flex items-start gap-3">
              <StatusIcon status={project.status} />
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg lg:text-xl font-semibold text-gray-900 group-hover:text-primary transition-colors duration-200 truncate">
                  {project.name}
                </h3>
                {project.description && (
                  <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">
                    {project.description}
                  </p>
                )}
              </div>
            </div>

            {/* Project Meta */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 lg:gap-4">
              <Badge
                variant={
                  project.status === "Completed"
                    ? "success"
                    : project.status === "In Progress"
                    ? "primary"
                    : project.status === "On Hold"
                    ? "warning"
                    : "danger"
                }
                className="flex items-center gap-1 text-xs"
              >
                {project.status}
              </Badge>

              {project.pic && (
                <div className="flex items-center gap-1 text-xs text-gray-600 bg-gray-50 px-1.5 py-1 rounded-md">
                  <User className="h-3 w-3" />
                  <span className="truncate max-w-16 sm:max-w-24 lg:max-w-none">
                    {project.pic}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Calendar className="h-3 w-3" />
                <span className="text-xs">
                  {new Date(project.created_at).toLocaleDateString()}
                </span>
              </div>

              <div className="flex items-center gap-1 bg-primary/10 text-primary px-1.5 py-1 rounded-md text-xs font-medium">
                <FileText className="h-3 w-3" />
                <span>
                  {reportCount} Report{reportCount !== 1 ? "s" : ""}
                </span>
              </div>
            </div>
          </div>

          {/* Right Section - Actions */}
          <div className="flex items-start gap-1 sm:gap-2 flex-shrink-0">
            {/* Action Buttons */}
            {!isSupervisor && (
              <div className="flex items-center gap-1 sm:gap-2">
                {/* Mobile: Icon only buttons */}
                <Button
                  variant="primary"
                  size="sm"
                  className="action-button flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 h-7 sm:h-8"
                  onClick={(e) =>
                    handleActionClick(
                      e,
                      () =>
                        (window.location.href = `/admin/projects/show/${project.id}`)
                    )
                  }
                >
                  <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Detail</span>
                </Button>

                <Button
                  variant="secondary"
                  size="sm"
                  className="action-button flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 h-7 sm:h-8"
                  onClick={(e) =>
                    handleActionClick(
                      e,
                      () =>
                        (window.location.href = `/admin/projects/map?projectId=${project.id}`)
                    )
                  }
                >
                  <Navigation className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden lg:inline text-xs sm:text-sm">
                    Show in Map
                  </span>
                  <span className="inline lg:hidden text-xs sm:text-sm">
                    Map
                  </span>
                </Button>

                <div className="relative overflow-visible group/menu action-button">
                  <div
                    className="action-button p-1.5 sm:p-2 h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center rounded-lg sm:rounded-xl bg-white text-primary-dark border border-gray-200 hover:border-primary/40 hover:bg-primary/5 cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
                  </div>

                  {/* Dropdown Menu */}
                  <div className="dropdown-menu absolute right-0 top-full mt-1 sm:mt-3 w-32 sm:w-36 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover/menu:opacity-100 group-hover/menu:visible transition-all duration-200 z-10">
                    <button
                      onClick={(e) =>
                        handleActionClick(
                          e,
                          () =>
                            (window.location.href = `/admin/projects/edit/${project.id}`)
                        )
                      }
                      className="w-full px-2 sm:px-3 py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 first:rounded-t-lg transition-colors duration-150"
                    >
                      <Edit className="h-3 w-3 sm:h-4 sm:w-4" /> Edit
                    </button>
                    <button
                      onClick={(e) =>
                        handleActionClick(e, () => onDeleteClick(project))
                      }
                      className="w-full px-2 sm:px-3 py-2 text-left text-xs sm:text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 last:rounded-b-lg transition-colors duration-150"
                    >
                      <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Expand/Collapse Indicator */}
            <div className="flex flex-col items-center justify-center p-1.5 sm:p-2 h-auto sm:h-8 w-auto sm:w-8 text-gray-400 group-hover:text-gray-600 transition-colors duration-200">
              {isExpanded ? (
                <>
                  <div className="flex items-center gap-2">
                    <ChevronUp className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="text-xs mt-0.5 sm:hidden">Hide</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span className="text-xs mt-0.5 sm:hidden">
                      Show Reports
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expandable Reports Section */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50/50">
          <div className="p-4 sm:p-5 lg:p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
              <h4 className="font-medium text-gray-900 flex items-center gap-2 text-sm sm:text-base">
                <FileText className="h-4 w-4" /> Project Reports ({reportCount})
              </h4>
              {!isSupervisor && (
                <Button
                  variant="primary"
                  size="sm"
                  className="action-button flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 h-7 sm:h-8 w-full sm:w-auto justify-center"
                  onClick={(e) =>
                    handleActionClick(
                      e,
                      () =>
                        (window.location.href = `/admin/reports/add/${project.id}`)
                    )
                  }
                >
                  <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">Add Report</span>
                </Button>
              )}
            </div>

            {reports && reports.length > 0 ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2 sm:gap-3">
                {reports.map((report) => (
                  <div key={report.id} onClick={(e) => e.stopPropagation()}>
                    <ReportItem
                      report={report}
                      onOpenReportModal={onOpenReportModal}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={FileText}
                message="No reports found for this project"
                size="sm"
                action={
                  !isSupervisor
                    ? {
                        label: "Create First Report",
                        onClick: (e) => {
                          e.stopPropagation();
                          window.location.href = `/admin/reports/add/${project.id}`;
                        },
                      }
                    : null
                }
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCard;
